import React, { useState } from 'react';
import {
  AlertTriangle,
  Trash2,
  RefreshCw,
  ExternalLink,
  CheckCircle2,
  Loader2
} from 'lucide-react';
import GreyCardBadge from '../cards/GreyCardBadge';
import { formatVND } from '../../utils/formatters';

export default function GreyCardsTab({
  greyCards = [],
  greyProducts = [],
  onDeleteSingle,
  onPurgeAll,
  scanner
}) {
  const items = greyCards.length > 0 ? greyCards : greyProducts;
  const [showConfirmPurge, setShowConfirmPurge] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const isScanning = scanner?.isScanning || false;
  const handleScanAll = () => {
    if (scanner?.triggerScan) scanner.triggerScan();
  };

  const handleDelete = async (id) => {
    setDeletingId(id);
    await onDeleteSingle(id);
    setDeletingId(null);
  };

  const handleConfirmPurge = async () => {
    setShowConfirmPurge(false);
    await onPurgeAll();
  };

  return (
    <div className="space-y-4 text-xs select-none">
      {/* Header Actions Bar */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-3.5 bg-[#2B0509] rounded-xl border border-[#520B12]">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-lg bg-amber-950/80 text-[#F5A623] border border-[#F5A623]/30 flex items-center justify-center">
            <AlertTriangle size={16} />
          </div>
          <div>
            <h4 className="text-xs font-bold text-[#FFE29A]">
              Quản lý thẻ hỏng & hết hàng (Grey Cards)
            </h4>
            <p className="text-[11px] text-amber-200/60">
              Hiện có <strong className="text-amber-400">{items.length}</strong> sản phẩm bị đánh dấu hết hàng hoặc lỗi liên kết.
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          {/* Scan All Links Button */}
          <button
            onClick={handleScanAll}
            disabled={isScanning}
            className="px-3 py-1.5 rounded-xl border border-[#6E141E] bg-[#34070B] hover:bg-[#480C12] text-amber-200 text-xs font-semibold flex items-center gap-1.5 transition disabled:opacity-50 shadow-xs"
          >
            {isScanning ? (
              <>
                <Loader2 size={13} className="animate-spin text-[#F5A623]" />
                <span>Đang quét...</span>
              </>
            ) : (
              <>
                <RefreshCw size={13} className="text-[#F5A623]" />
                <span>Quét lại toàn bộ link</span>
              </>
            )}
          </button>

          {/* Delete All Grey Cards Button */}
          {items.length > 0 && (
            <button
              onClick={() => setShowConfirmPurge(true)}
              className="px-3 py-1.5 rounded-xl bg-rose-700 hover:bg-rose-800 text-white text-xs font-bold flex items-center gap-1.5 shadow-sm transition"
            >
              <Trash2 size={13} />
              <span>Xóa sạch tất cả thẻ hỏng</span>
            </button>
          )}
        </div>
      </div>

      {/* Confirmation Modal for Purge All */}
      {showConfirmPurge && (
        <div className="p-3.5 rounded-xl bg-rose-950/80 border border-rose-700/60 space-y-2">
          <div className="text-xs font-bold text-rose-200">
            Xác nhận xóa vĩnh viễn {items.length} sản phẩm thẻ xám khỏi hệ thống?
          </div>
          <p className="text-[11px] text-rose-300">
            Hành động này sẽ gỡ bỏ tất cả các sản phẩm hết hàng hoặc link chết.
          </p>
          <div className="flex gap-2 justify-end pt-1">
            <button
              onClick={() => setShowConfirmPurge(false)}
              className="px-3 py-1 rounded-lg border border-[#520B12] bg-[#2B0509] text-xs font-medium text-amber-200"
            >
              Hủy
            </button>
            <button
              onClick={handleConfirmPurge}
              className="px-3 py-1 rounded-lg bg-rose-600 text-white text-xs font-bold hover:bg-rose-700"
            >
              Đồng ý xóa sạch
            </button>
          </div>
        </div>
      )}

      {/* Grey Cards List */}
      <div className="border border-[#4A0A10] rounded-xl overflow-hidden divide-y divide-[#3A080D]">
        {items.length === 0 ? (
          <div className="p-8 text-center text-xs text-amber-200/50">
            <CheckCircle2 size={24} className="text-emerald-500 mx-auto mb-2 opacity-80" />
            <p className="font-semibold text-amber-200">Tất cả liên kết đều hoạt động tốt!</p>
            <p className="text-[11px] text-amber-200/50 mt-0.5">Không có sản phẩm nào bị lỗi hoặc hết hàng.</p>
          </div>
        ) : (
          items.map(p => (
            <div key={p.id} className="p-3 flex items-center justify-between gap-3 hover:bg-[#2B0509] transition">
              <div className="flex items-center gap-3 min-w-0">
                <img
                  src={p.image_url}
                  alt=""
                  referrerPolicy="no-referrer"
                  className="w-10 h-10 object-cover rounded-lg border border-[#520B12] shrink-0 grayscale opacity-60"
                />
                <div className="min-w-0">
                  <div className="text-xs font-bold text-[#FFF5EB] truncate flex items-center gap-2">
                    <span className="truncate">{p.title}</span>
                    <GreyCardBadge reason={p.grey_reason} size="sm" />
                  </div>
                  <div className="text-[11px] text-amber-200/60 flex items-center gap-2 mt-0.5">
                    <span className="font-bold text-[#FFE29A]">{formatVND(p.price_vnd || p.price)}</span>
                    <span>•</span>
                    <span className="uppercase font-semibold">{p.platform}</span>
                    <span>•</span>
                    <span>{p.category_name}</span>
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-2 shrink-0">
                <a
                  href={p.affiliate_url || p.target_url}
                  target="_blank"
                  rel="noreferrer"
                  className="p-1.5 text-amber-200/60 hover:text-amber-200 rounded hover:bg-[#3A080D]"
                  title="Kiểm tra link gốc"
                >
                  <ExternalLink size={13} />
                </a>
                <button
                  onClick={() => handleDelete(p.id)}
                  disabled={deletingId === p.id}
                  className="p-1.5 text-rose-400 hover:text-rose-200 rounded hover:bg-rose-950/40 transition"
                  title="Xóa thẻ này"
                >
                  <Trash2 size={13} />
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
